import { createStubImplementation, affirmSingleton } from "@ardoq/stub-implementation";
import * as fnsLocales from "date-fns/locale";
const locales = {
  ...fnsLocales,
  deCH: { ...fnsLocales.de, code: "de-CH" }
};
const localeList = Object.values(locales).filter((locale) => locale.code).map((locale) => ({
  locale,
  code: locale.code,
  name: new Intl.DisplayNames(locale.code, { type: "language" }).of(
    locale.code
  )
}));
const ARDOQ_DEFAULT_LOCALE = locales.enUS;
const defaultImplementation = {
  getCurrentLocale: () => ARDOQ_DEFAULT_LOCALE
};
const { getCurrentLocale, registerImplementation } = createStubImplementation("@ardoq/locale", defaultImplementation);
const getLocale = (localeCode) => {
  if (!localeCode) return void 0;
  const localeData = localeList.find(
    ({ code }) => code.toLowerCase() === localeCode.toLowerCase()
  );
  return localeData == null ? void 0 : localeData.locale;
};
const constructCollator = (locale, options) => {
  const supportedLocales = Intl.Collator.supportedLocalesOf(locale.code);
  return new Intl.Collator(
    supportedLocales.length ? supportedLocales : ARDOQ_DEFAULT_LOCALE.code,
    options
  );
};
const createCachedCollatorGetter = (options) => {
  let localeCache = ARDOQ_DEFAULT_LOCALE;
  let collatorCache = constructCollator(ARDOQ_DEFAULT_LOCALE, options);
  return (locale) => {
    if (locale !== localeCache) {
      localeCache = locale;
      collatorCache = constructCollator(locale, options);
    }
    return collatorCache;
  };
};
const getStrictCollator = createCachedCollatorGetter();
const getLowercaseCollator = createCachedCollatorGetter({
  sensitivity: "accent"
});
const getNumericCollator = createCachedCollatorGetter({
  numeric: true
});
const getNumericLowercaseCollator = createCachedCollatorGetter({
  numeric: true,
  sensitivity: "accent"
});
const getBaseCollator = createCachedCollatorGetter({
  sensitivity: "base"
});
const getInsensitiveCollator = createCachedCollatorGetter({
  sensitivity: "base",
  ignorePunctuation: true
});
const getDefaultLocale = (language = navigator.language) => {
  return getLocale(language) ?? ARDOQ_DEFAULT_LOCALE;
};
const currencies = [
  {
    code: "USD",
    label: "$ - US Dollar"
  },
  {
    code: "CAD",
    label: "CA$ - Canadian Dollar"
  },
  {
    code: "AUD",
    label: "A$ - Australian Dollar"
  },
  {
    code: "MXN",
    label: "MX$ - Mexican Peso"
  },
  {
    code: "EUR",
    label: "€ - Euro"
  },
  {
    code: "DKK",
    label: "DKK - Danish Krone"
  },
  {
    code: "NOK",
    label: "NOK - Norwegian Krone"
  },
  {
    code: "SEK",
    label: "SEK - Swedish Krona"
  },
  {
    code: "GBP",
    label: "£ - Pound Sterling"
  },
  {
    code: "BRL",
    label: "BRL - Brazilian Real"
  },
  {
    code: "CHF",
    label: "CHF - Swiss Franc"
  },
  {
    code: "JPY",
    label: "¥ - Yen"
  },
  {
    code: "NZD",
    label: "NZD - New Zealand Dollar"
  }
];
const numberFormatCache = {};
const mapCurrencyToNumberFormatOption = () => currencies.map(({ code, label }) => [
  `currency${code}`,
  {
    val: `currency${code}`,
    option: { style: "currency", currency: code },
    abbreviatedOption: {
      style: "currency",
      currency: code,
      notation: "compact",
      maximumFractionDigits: 0
    },
    label,
    category: "currency"
  }
]);
const numberFormatOptions = new Map([
  [
    "default",
    {
      val: "default",
      option: {},
      abbreviatedOption: { notation: "compact", maximumFractionDigits: 0 },
      label: "Default",
      category: ""
    }
  ],
  [
    "decimal",
    {
      val: "decimal",
      option: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
      abbreviatedOption: { notation: "compact", maximumFractionDigits: 0 },
      label: "Decimal",
      category: ""
    }
  ],
  ...mapCurrencyToNumberFormatOption()
]);
const getNumberFormatIdentifiers = () => {
  return Array.from(numberFormatOptions.keys());
};
const getNumberFormat = (formatOption, locale = ARDOQ_DEFAULT_LOCALE) => {
  const key = JSON.stringify([formatOption, locale.code]);
  if (!numberFormatCache[key]) {
    numberFormatCache[key] = new Intl.NumberFormat(locale.code, formatOption);
  }
  return numberFormatCache[key];
};
const formatNumber = (number, option = "default", locale = ARDOQ_DEFAULT_LOCALE, isAbbreviated = false) => {
  var _a;
  if (number === void 0 || number === null || isNaN(number)) {
    return "";
  }
  const optionName = isAbbreviated ? "abbreviatedOption" : "option";
  const numberFormatOption = ((_a = numberFormatOptions.get(option)) == null ? void 0 : _a[optionName]) ?? {};
  return getNumberFormat(numberFormatOption, locale).format(number);
};
const localeCompare = (a, b, locale) => {
  return getStrictCollator(locale).compare(a, b);
};
const localeCompareNumeric = (a, b, locale) => {
  return getNumericCollator(locale).compare(a, b);
};
const localeCompareNumericLowercase = (a, b, locale) => {
  return getNumericLowercaseCollator(locale).compare(a, b);
};
const localeCompareBase = (a, b, locale) => {
  return getBaseCollator(locale).compare(a, b);
};
const localeCompareInsensitive = (a, b, locale) => {
  return getInsensitiveCollator(locale).compare(a, b);
};
const localeAreEqualLowercase = (a, b, locale) => {
  return getLowercaseCollator(locale).compare(a, b) === 0;
};
const localeIncludesLowercase = (value, searchPhrase, locale) => {
  const searchPhraseLowercase = searchPhrase.toLocaleLowerCase(locale.code);
  return value.toLocaleLowerCase(locale.code).includes(searchPhraseLowercase);
};
const localeIndexOfLowercase = (value, searchPhrase, locale) => {
  const searchPhraseLowercase = searchPhrase.toLocaleLowerCase(locale.code);
  return value.toLocaleLowerCase(locale.code).indexOf(searchPhraseLowercase);
};
affirmSingleton("@ardoq/locale");
export {
  ARDOQ_DEFAULT_LOCALE,
  defaultImplementation,
  formatNumber,
  getBaseCollator,
  getCurrentLocale,
  getDefaultLocale,
  getInsensitiveCollator,
  getLocale,
  getLowercaseCollator,
  getNumberFormat,
  getNumberFormatIdentifiers,
  getNumericCollator,
  getNumericLowercaseCollator,
  getStrictCollator,
  localeAreEqualLowercase,
  localeCompare,
  localeCompareBase,
  localeCompareInsensitive,
  localeCompareNumeric,
  localeCompareNumericLowercase,
  localeIncludesLowercase,
  localeIndexOfLowercase,
  localeList,
  locales,
  numberFormatOptions,
  registerImplementation
};
//# sourceMappingURL=index.js.map
